import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useIonRouter } from "@ionic/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import { useModal } from "../contexts/ModalContext";
import { ArrowLeft } from "lucide-react";
import api, { logoutRequest, clearAuthSession } from "../services/api";
import { GlassCard } from "../components/ui/glass-card";
import { Spinner } from "@radix-ui/themes";
import { PasswordRequirementsChecklist } from "../components/PasswordRequirementsChecklist";
import {
  describeMissingRequirements,
  validatePassword,
} from "../utils/passwordPolicy";

let NativeBiometric: any;
try {
  NativeBiometric =
    require("..capgo/capacitor-native-biometric").NativeBiometric;
} catch {}

const Settings: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const history = useHistory();
  const ionRouter = useIonRouter();
  const { showConfirm } = useModal();
  const { toast } = useToast();

  const [isLoadingChangePassword, setIsLoadingChangePassword] = useState(false);

  const changePassword = async () => {
    const policyError = describeMissingRequirements(newPassword);
    if (policyError) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: policyError,
      });
      return;
    }
    setIsLoadingChangePassword(true);
    try {
      await api.post("/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast({
        title: "Password aggiornata",
        description: "La tua password è stata aggiornata con successo.",
      });
      setCurrentPassword("");
      setNewPassword("");
    } catch {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Errore nell'aggiornamento della password",
      });
    }
    setIsLoadingChangePassword(false);
  };

  const logout = async () => {
    await logoutRequest();
    try {
      if (NativeBiometric) {
        NativeBiometric.deleteCredentials({
          server: "digital-card",
        });
      }
    } catch {}
    // Svuota lo stack Ionic (es. Loading) che altrimenti continua a chiamare /me
    ionRouter.push("/login", "root", "replace");
  };

  const deleteAccount = async () => {
    showConfirm(
      "Elimina Account",
      "Sei sicuro di voler eliminare il tuo account? Questa azione è irreversibile.",
      async () => {
        try {
          await api.delete("/me");
          clearAuthSession();
          try {
            if (NativeBiometric) {
              NativeBiometric.deleteCredentials({
                server: "digital-card",
              });
            }
          } catch {}
          history.replace("/register");
        } catch {
          toast({
            variant: "destructive",
            title: "Errore",
            description: "Errore durante l'eliminazione dell'account",
          });
        }
      },
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <GlassCard className="relative w-full max-w-md rounded-2xl p-4 pt-12 space-y-4">
        {/* Pulsante "Ritorna indietro" all’interno della card */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 rounded-full"
          onClick={() => history.push("/create")}
        >
          <ArrowLeft className="size-6 text-white" />
        </Button>

        <h2 className="gradient-text text-2xl font-bold text-center">
          Impostazioni Account
        </h2>

        {/* Cambio password */}
        <div className="space-y-4">
          <div className="w-full rounded-xl border border-white/10 p-6 space-y-6">
            <span className="font-semibold">Cambia password</span>

            <div>
              <Label htmlFor="currentPassword">Password attuale</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Inserisci la password attuale"
                className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Nuova password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Inserisci la nuova password"
                className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
              />
              <PasswordRequirementsChecklist password={newPassword} />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-teal-200 to-teal-500 text-white hover:opacity-90"
                onClick={changePassword}
                disabled={
                  isLoadingChangePassword ||
                  !currentPassword ||
                  !validatePassword(newPassword).valid
                }
              >
                {isLoadingChangePassword && <Spinner />}
                Cambia Password
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Button
              variant="outline"
              size="lg"
              className="w-full backdrop-blur-xl bg-white/5 border border-white/10 text-white hover:opacity-80"
              onClick={logout}
            >
              Logout
            </Button>
          </div>

          <Button
            size="lg"
            className="w-full bg-red-600 text-white hover:bg-red-700"
            onClick={deleteAccount}
          >
            Elimina Account
          </Button>
        </div>
      </GlassCard>
    </div>
  );
};

export default Settings;
